import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
    StepErrors,
    TouchedFields,
} from '../subviews/draft-case-subview/techline-draft-stepper/techline-draft-stepper.types';
import type { WorksheetGroup } from '../worksheet-group-accordion/worksheet-group-accordion.types';

import {
    getRequiredFields,
    isFieldEmpty,
    validateConcernDetails,
    validateTechDescription,
    validateWorksheets,
} from '../subviews/draft-case-subview/techline-draft-stepper/techline-draft-stepper.validators';

/** The return type of useDraftStepperValidation, threaded as a single prop through the component tree. */
export type DraftStepperValidation = {
    /** All field-level errors, keyed by step then field. */
    errors: StepErrors;

    /** Callback fired before the stepper transitions between steps. */
    handleBeforeStepChange: (fromIndex: number, toIndex: number) => void;

    /** Callback to mark a single field as touched within a step. */
    handleTouchedChange: (step: keyof TouchedFields, field: string) => void;

    /** Initializes visited steps and touched fields up to the given step index. */
    initializeForStep: (step: number) => void;

    /** Per-step validation summary used by stepper header icons and submit gating. */
    stepValidation: StepValidationSummary;

    /** Current touched state for each step, keyed by field name. */
    touched: TouchedFields;

    /** Set of step indices the user has navigated to. */
    visitedSteps: Set<number>;
};

/** Per-step validation status used by the stepper header and submit gating. */
export type StepValidationSummary = {
    /** Whether the case has at least one completed worksheet submission. */
    hasCompletedWorksheets: boolean;

    /** Whether all required concern details fields are filled and error-free. */
    isConcernDetailsComplete: boolean;

    /** Whether the concern details step has validation errors. */
    isConcernDetailsInvalid: boolean;

    /** Whether the entire form is valid and ready for submission. */
    isFormValid: boolean;

    /** Whether all required technical description fields are filled and error-free. */
    isTechnicalDescriptionComplete: boolean;

    /** Whether the technical description step has validation errors. */
    isTechnicalDescriptionInvalid: boolean;

    /** Whether the worksheets step is complete (has worksheets and no errors). */
    isWorksheetsComplete: boolean;

    /** Whether the worksheets step has validation errors. */
    isWorksheetsInvalid: boolean;
};

const START_STEP_INDEX = 0;
const WORKSHEETS_STEP_INDEX = 2;

/** Mapping from a step index to the keys needed for touched-state lookup and required-fields config lookup. */
type StepFieldMapping = {
    /** The key in TouchedFields corresponding to this step. */
    touchedKey: keyof TouchedFields;

    /** The key in STEP_RULES corresponding to this step's validation config. */
    configKey: string;
};

/** Maps each non-worksheet step index to its touched-state key and validation config key. */
const STEP_FIELD_MAP: Record<number, StepFieldMapping> = {
    0: { touchedKey: 'concernDetailsStep', configKey: 'concernDetails' },
    1: { touchedKey: 'technicalDescriptionStep', configKey: 'techDescription' },
};

/**
 * Hook that manages all draft stepper validation state: touched fields, visited steps,
 * and field-level errors (computed from caseDetails).
 * Lives in useTechlineCase so its state survives the stepper unmounting when the
 * worksheet editor is opened (CaseManagementView swaps subviews).
 *
 * @param caseDetails - the current Techline case entity, or undefined if not yet loaded
 * @param worksheetGroupData - the worksheet groups attached to the case, or undefined if not yet loaded
 * @param worksheetValidationResults - per-submission validation pass/fail map, keyed by submissionId
 * @returns validation state and callbacks for the draft stepper
 */
export function useDraftStepperValidation(
    caseDetails: TechlineCaseEntity | undefined,
    worksheetGroupData: WorksheetGroup[] | undefined,
    worksheetValidationResults: Record<string, boolean> | undefined
): DraftStepperValidation {
    const [touched, setTouched] = useState<TouchedFields>({
        concernDetailsStep: {},
        technicalDescriptionStep: {},
        worksheetsStep: {},
    });

    const [visitedSteps, setVisitedSteps] = useState<Set<number>>(() => {
        const initial = new Set<number>();
        initial.add(START_STEP_INDEX);
        return initial;
    });

    const hasAutoValidated = useRef(false);

    const hasCompletedWorksheets = (caseDetails?.WorksheetSubmissionGroups?.length ?? 0) > 0;

    /**
     * Field-level errors for all steps based on the current case details state and worksheet validation results.
     */
    const errors = useMemo((): StepErrors => {
        if (!caseDetails) {
            return { concernDetailsStep: {}, technicalDescriptionStep: {}, worksheetsStep: {} };
        }
        return {
            concernDetailsStep: validateConcernDetails(caseDetails),
            technicalDescriptionStep: validateTechDescription(caseDetails),
            worksheetsStep: validateWorksheets(worksheetGroupData, worksheetValidationResults, hasCompletedWorksheets),
        };
    }, [caseDetails, worksheetGroupData, worksheetValidationResults, hasCompletedWorksheets]);

    // -- Handlers --

    const handleTouchedChange = useCallback((step: keyof TouchedFields, field: string) => {
        setTouched((prev) => ({
            ...prev,
            [step]: { ...prev[step], [field]: true },
        }));
    }, []);

    const touchAllFieldsForSteps = useCallback(
        (stepIndices: number[]) => {
            if (!caseDetails) {
                return;
            }
            setTouched((prev) => {
                const next = { ...prev };
                for (const index of stepIndices) {
                    const mapping = STEP_FIELD_MAP[index];
                    if (!mapping) {
                        continue;
                    }
                    const fields = getRequiredFields(mapping.configKey, caseDetails);
                    next[mapping.touchedKey] = {
                        ...next[mapping.touchedKey],
                        ...Object.fromEntries(fields.map((f) => [f, true])),
                    };
                }
                return next;
            });
        },
        [caseDetails]
    );

    const handleBeforeStepChange = useCallback(
        (fromIndex: number, toIndex: number) => {
            setVisitedSteps((prev) => {
                if (prev.has(toIndex)) {
                    return prev;
                }
                const next = new Set(prev);
                next.add(toIndex);
                return next;
            });
            if (toIndex > 0) {
                const stepsToValidate = Array.from({ length: toIndex }, (_, i) => i);
                touchAllFieldsForSteps(stepsToValidate);
            }
        },
        [touchAllFieldsForSteps]
    );

    /**
     * Called once on stepper mount to handle page refresh scenarios.
     * Marks steps 0 through the given step as visited and touches all
     * required fields on those steps so validation runs immediately.
     */
    const initializeForStep = useCallback(
        (step: number) => {
            if (step <= 0) {
                return;
            }

            setVisitedSteps((prev) => {
                const next = new Set(prev);
                for (let i = 0; i <= step; i++) {
                    next.add(i);
                }
                return next;
            });

            const stepsToTouch = Array.from({ length: step + 1 }, (_, i) => i);
            touchAllFieldsForSteps(stepsToTouch);
        },
        [touchAllFieldsForSteps]
    );

    // -- Auto-validate existing cases --

    /**
     * Runs once when caseDetails first arrives. Detects whether this is an existing
     * case (user previously saved/exited) by checking if any required field has a value
     * or if any worksheets exist. If so, touches all fields on all steps and marks all
     * steps visited so validation icons and errors appear immediately.
     *
     * Brand-new cases (all fields empty, no worksheets) use progressive disclosure —
     * icons and errors only appear after user interaction or navigation.
     */
    useEffect(() => {
        if (hasAutoValidated.current || !caseDetails) {
            return;
        }
        hasAutoValidated.current = true;

        const hasAnyRequiredFieldValue = Object.values(STEP_FIELD_MAP).some(({ configKey }) => {
            const fields = getRequiredFields(configKey, caseDetails);
            return fields.some((field) => {
                const value = (caseDetails as unknown as Record<string, unknown>)[field];
                return !isFieldEmpty(value);
            });
        });

        const hasWorksheets = (caseDetails.WorksheetSubmissionGroups?.length ?? 0) > 0;

        if (hasAnyRequiredFieldValue || hasWorksheets) {
            const allStepIndices = Object.keys(STEP_FIELD_MAP).map(Number);
            touchAllFieldsForSteps(allStepIndices);
            setVisitedSteps(new Set([0, 1, WORKSHEETS_STEP_INDEX]));
        }
    }, [caseDetails, touchAllFieldsForSteps]);

    // -- Step Validation Summary --

    const stepValidation = useMemo((): StepValidationSummary => {
        const concernHasErrors = Object.keys(errors.concernDetailsStep).length > 0;
        const techHasErrors = Object.keys(errors.technicalDescriptionStep).length > 0;
        const worksheetHasErrors = Object.keys(errors.worksheetsStep).length > 0;

        // A step is complete when its validator produces zero errors.
        const isConcernDetailsComplete = !concernHasErrors;
        const isTechnicalDescriptionComplete = !techHasErrors;
        const isWorksheetsComplete = !worksheetHasErrors;

        const isConcernDetailsInvalid = Object.entries(errors.concernDetailsStep).some(
            ([field, error]) => Boolean(error) && touched.concernDetailsStep[field]
        );
        const isTechnicalDescriptionInvalid = Object.entries(errors.technicalDescriptionStep).some(
            ([field, error]) => Boolean(error) && touched.technicalDescriptionStep[field]
        );
        const isWorksheetsInvalid = visitedSteps.has(WORKSHEETS_STEP_INDEX) && worksheetHasErrors;

        const isFormValid = isConcernDetailsComplete && isTechnicalDescriptionComplete && isWorksheetsComplete;

        return {
            hasCompletedWorksheets,
            isConcernDetailsComplete,
            isConcernDetailsInvalid,
            isFormValid,
            isTechnicalDescriptionComplete,
            isTechnicalDescriptionInvalid,
            isWorksheetsComplete,
            isWorksheetsInvalid,
        };
    }, [errors, touched, visitedSteps, hasCompletedWorksheets]);

    return {
        errors,
        stepValidation,
        touched,
        visitedSteps,
        handleTouchedChange,
        handleBeforeStepChange,
        initializeForStep,
    };
}
